import { Api } from '@axiom/ui';
import { EducationEntity } from '@axiom/domain';
import type {
  Candidate,
  CandidateBio,
  CandidateReputation,
  CandidateReputationDetailsRequestBody,
  CandidateReputationDetailsResponseBody,
  EnvoyCandidateSearchFilters,
  GroupedExperiences,
} from '@axiom/validation';

import { EnvUtil } from '../utils/env-util';

export type ReadCandidatesResponseType = {
  data: Candidate[];
  meta: {
    resultCount: number;
    currentPage: number;
    pageCount: number;
  };
};

class CandidateApiClass extends Api {
  // Aborted in clearReadCandidates() so the in-flight non-AI search is
  // actually cancelled at the network layer (only effective on the fetch path;
  // superagent ignores the signal).
  private readCandidatesAbortController?: AbortController;

  constructor() {
    super({
      domain: EnvUtil.apiEnvoyUrl,
    });
  }

  readCandidate(
    candidateId: Candidate['id'],
    query: { isAnonymous?: boolean } = { isAnonymous: false }
  ) {
    // Normalize the degrees through EducationEntity so each education shares
    // the Data Client store and resolves its `institution` through the linked
    // LawSchool record. A LawSchool write (e.g. an admin rename) then updates
    // the displayed institution reactively, with no invalidation.
    return super.read<{ data: Candidate }>({
      endpoint: `/candidates/${candidateId}`,
      method: 'GET',
      query: {
        isAnonymous: query.isAnonymous,
      },
      schema: { data: { degrees: [EducationEntity] } },
    });
  }

  readBio({
    candidateId,
    isAnonymous = false,
  }: {
    candidateId: Candidate['id'];
    isAnonymous?: boolean;
  }) {
    return super.read<{ data: CandidateBio }>({
      endpoint: `/candidates/${candidateId}/bio`,
      method: 'GET',
      query: { isAnonymous },
    });
  }

  async updateCandidate(
    candidateId: Candidate['id'],
    candidate: Partial<Candidate>
  ) {
    return super.write<{ data: Candidate }>({
      endpoint: `/candidates/${candidateId}`,
      method: 'PATCH',
      body: candidate,
    });
  }

  readAiSearchCandidates({
    searchRequest,
    signal,
  }: {
    searchRequest: string;
    signal?: AbortSignal;
  }) {
    return super.read<ReadCandidatesResponseType>({
      endpoint: '/candidates/search/ai',
      method: 'POST',
      body: { searchRequest },
      signal,
      useFetch: true,
    });
  }

  async refreshAiSearchCandidates() {
    return super.invalidate('/candidates/search/ai', true);
  }

  clearAiSearchCandidates() {
    super.clear('/candidates/search/ai');
  }

  readCandidates({
    filters: filtersProp,
    sort,
    page = 1,
    pageSize,
    order,
  }: {
    filters?: EnvoyCandidateSearchFilters;
    sort?: string;
    page?: number;
    pageSize?: number;
    order?: string | null | undefined;
  } = {}) {
    const filters = filtersProp ?? { search: undefined };
    const { search } = filters;
    const filtersExcludingSearch = Object.fromEntries(
      Object.entries(filters).filter(([key]) => key !== 'search')
    );

    if (!this.readCandidatesAbortController) {
      this.readCandidatesAbortController = new AbortController();
    }

    return super.read<ReadCandidatesResponseType>({
      endpoint: '/candidates/search',
      method: 'POST',
      body: {
        filters: filtersExcludingSearch,
        sort,
        page,
        pageSize,
        order: order ?? undefined,
        search,
      },
      signal: this.readCandidatesAbortController.signal,
    });
  }

  readCandidateGroupedExperiences({
    candidateId,
    query,
  }: {
    candidateId: Candidate['id'];
    query?: { isAnonymous: boolean };
  }) {
    return super.read<{ data: GroupedExperiences[] }>({
      endpoint: `/candidates/${candidateId}/groupedExperiences`,
      query,
      method: 'GET',
    });
  }

  async refreshCandidates() {
    return super.invalidate('/candidates/search');
  }

  async refreshCandidate({ candidateId }: { candidateId: Candidate['id'] }) {
    return super.invalidate(`/candidates/${candidateId}`);
  }

  readCandidateReputation({ candidateId }: { candidateId: Candidate['id'] }) {
    return super.read<{ data: CandidateReputation | null }>({
      endpoint: `/candidates/${candidateId}/reputation`,
      method: 'GET',
    });
  }

  readCandidateReputationDetails({
    candidateId,
    body,
  }: {
    candidateId: Candidate['id'];
    body: CandidateReputationDetailsRequestBody;
  }) {
    return super.read<CandidateReputationDetailsResponseBody>({
      endpoint: `/candidates/${candidateId}/reputationDetails`,
      method: 'POST',
      body,
    });
  }

  async refreshCandidateReputation({
    candidateId,
  }: {
    candidateId: Candidate['id'];
  }) {
    return super.invalidate(`/candidates/${candidateId}/reputation`);
  }

  async refreshCandidateReputationDetails({
    candidateId,
  }: {
    candidateId: Candidate['id'];
  }) {
    return super.invalidate(`/candidates/${candidateId}/reputationDetails`);
  }

  clearReadCandidates() {
    this.readCandidatesAbortController?.abort();
    this.readCandidatesAbortController = undefined;
    super.clear('/candidates/search');
  }
}

export const CandidateApi = new CandidateApiClass();
